import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import { citationLiteratureFormatter } from '@Services/FormatUtils';
import externalUrl from '@Utils/ExternalUrl';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import Catalyst from '@Services/Catalyst';
import { sectionsList, getFirstAvailableSection } from '@Components/Results/documents/DocumentExpandSections';
import DocumentButtons from '@Components/Results/documents/DocumentButtons';
import DocumentCollapsibleContent from '@Components/Results/documents/DocumentCollapsibleContent';

import ViewFullPatentFamily from '@Containers/PatentFamilyClustering/Components/ViewFullPatentFamily';
import PatentContentContext from './PatentContentContext';
import PatentTitleMainFamilyMember from './PatentTitleMainFamilyMember';
import PatentTitleFamilyMember from './PatentTitleFamilyMember';
import PatentTitleSinglePatent from './PatentTitleSinglePatent';
import ProcessingModeWrapper from './ProcessingModeWrapper';

export const initExpandedFlagsState = () =>
  sectionsList.reduce(
    (acc, section) => ({
      ...acc,
      [section]: false,
    }),
    {}
  );

function PatentContent({
  document,
  metadata,
  zoom,
  isMainFamilyMember,
  isSinglePatent,
  getTrackingAttrs,
  firstSectionExpanded,
  setFirstSectionExpanded,
  viewFullPatentFamily,
}) {
  const firstExpandSection = useMemo(() => getFirstAvailableSection(document), [document]);
  const [expandedFlags, setExpandedFlags] = useState(initExpandedFlagsState);
  const [updatedCitedByUrl, setUpdatedCitedByUrl] = useState('');

  // abstractExpanded for main family member is controlled in parent
  useEffect(() => {
    if (!isUndefined(firstSectionExpanded)) {
      setExpandedFlags((_expandedFlags) => ({
        ..._expandedFlags,
        [firstExpandSection]: firstSectionExpanded,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSectionExpanded]);

  const toggleSection = (event, expandFlag) => {
    const isCurrentlyExpanded = expandedFlags[expandFlag];

    if (event) {
      event.preventDefault();
    }

    // abstractExpanded (or whatever is first section) for main document is controlled in parent
    if (expandFlag === firstExpandSection && isFunction(setFirstSectionExpanded)) {
      setFirstSectionExpanded(!isCurrentlyExpanded);
      return;
    }

    setExpandedFlags((_expandedFlags) => ({
      ..._expandedFlags,
      [expandFlag]: !isCurrentlyExpanded,
    }));
  };

  const onTitleClick = (event) => {
    toggleSection(event, firstExpandSection);
  };

  useEffect(() => {
    Catalyst.appendVisitorIDToUrl(externalUrl(document.citedBy)).then(setUpdatedCitedByUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { title: documentTitle } = document;
  const patentTitle = citationLiteratureFormatter(document.citationPatents);

  const contextValue = useMemo(
    () => ({
      document,
      updatedCitedByUrl,
      getTrackingAttrs,
    }),
    [document, updatedCitedByUrl, getTrackingAttrs]
  );

  return (
    <PatentContentContext.Provider value={contextValue}>
      <div className="patent-content u-els-margin-left u-els-font-size-meta">
        <div className="patent-content__element">
          {/* eslint-disable-next-line no-nested-ternary */}
          {isSinglePatent ? (
            <PatentTitleSinglePatent title={documentTitle} onClick={onTitleClick} getTrackingAttrs={getTrackingAttrs} />
          ) : isMainFamilyMember ? (
            <PatentTitleMainFamilyMember title={patentTitle} />
          ) : (
            <PatentTitleFamilyMember title={documentTitle} onClick={onTitleClick} getTrackingAttrs={getTrackingAttrs} />
          )}
          {!isMainFamilyMember && (
            <ProcessingModeWrapper>
              <InnerHTML tag="div" className="rx-element-literature" html={patentTitle} />
            </ProcessingModeWrapper>
          )}
          {document.assignees && (
            <div className="patent-content__current-patent-assignee">
              <span>Current Patent Assignee: </span>
              <InnerHTML tag="span" className="rx-element-assignee" html={document.assignees} />
            </div>
          )}
          <div className="patent-content__offices-row">
            {document.citationPatents && (
              <div>
                <span>{`Office${document.citationPatents.length > 1 ? 's' : ''}: `}</span>
                <InnerHTML
                  tag="span"
                  className="rx-element-assignee"
                  html={document.citationPatents.map((patent) => patent.patentCountryCode).join(', ')}
                />
              </div>
            )}
            {isSinglePatent && <ViewFullPatentFamily metadata={metadata} document={document} viewFullPatentFamily={viewFullPatentFamily} />}
          </div>
          <DocumentButtons
            document={document}
            toggleSection={toggleSection}
            getTrackingAttrs={getTrackingAttrs}
            metadata={metadata}
            {...expandedFlags}
          />
        </div>

        <DocumentCollapsibleContent
          metadata={metadata}
          document={document}
          zoom={metadata.isViewPreview ? 0 : zoom}
          toggleSection={toggleSection}
          getTrackingAttrs={getTrackingAttrs}
          {...expandedFlags}
        />
      </div>
    </PatentContentContext.Provider>
  );
}

PatentContent.propTypes = {
  // Own props
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  // Set in parent
  isMainFamilyMember: PropTypes.bool,
  isSinglePatent: PropTypes.bool,
  getTrackingAttrs: PropTypes.func.isRequired,
  firstSectionExpanded: PropTypes.bool,
  setFirstSectionExpanded: PropTypes.func,
  viewFullPatentFamily: PropTypes.func,
};

export default PatentContent;
